import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  setCsrfToken,
  type AuthMeResponse,
  type PublicConfig,
  type UserProfile,
} from '../services/api';

interface AuthContextValue {
  user: UserProfile | null;
  loading: boolean;
  config: PublicConfig | null;
  refresh: () => Promise<void>;
  login: (payload: Record<string, unknown>) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyAuthResponse(data: AuthMeResponse | undefined): UserProfile | null {
  if (data?.authenticated && data.user) {
    setCsrfToken(data.csrf_token ?? null);
    return data.user;
  }
  setCsrfToken(null);
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const res = await api.getMe();
    setUser(applyAuthResponse(res.data));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [configRes, meRes] = await Promise.all([
          api.getConfig(),
          api.getMe(),
        ]);
        if (configRes.data) setConfig(configRes.data);
        setUser(applyAuthResponse(meRes.data));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (payload: Record<string, unknown>) => {
    const res = await api.loginTelegram(payload);
    if (res.success && res.data?.authenticated) {
      setUser(applyAuthResponse(res.data));
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setCsrfToken(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, config, refresh, login, logout }),
    [user, loading, config, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
